/**
 * Salary structure import/export helpers (JSON v1).
 *
 * Format:
 * {
 *   version: 1,
 *   name: "Standard Staff",
 *   description: optional,
 *   components: [
 *     {
 *       code: "BASIC",           // preferred match key
 *       name: "Basic Pay",       // fallback match
 *       calculation_source: "fixed_amount" | "percentage" | ...,
 *       amount, percentage, percentage_of, reference, sequence
 *     }
 *   ]
 * }
 */

export const STRUCTURE_IMPORT_VERSION = 1;

export const SAMPLE_STRUCTURE = {
  version: STRUCTURE_IMPORT_VERSION,
  name: "Imported Standard Structure",
  description: "Sample structure — map components by code to your master list.",
  components: [
    {
      code: "BASIC",
      name: "Basic Pay",
      calculation_source: "percentage",
      percentage: 40,
      percentage_of: "CTC",
      sequence: 1,
    },
    {
      code: "HRA",
      name: "House Rent Allowance",
      calculation_source: "percentage",
      percentage: 50,
      percentage_of: "BASIC",
      sequence: 2,
    },
    {
      code: "SPECIAL",
      name: "Special Allowance",
      calculation_source: "fixed_amount",
      amount: 0,
      sequence: 3,
    },
    {
      code: "EPF_EE",
      name: "Employee PF",
      calculation_source: "statutory_engine",
      reference: "EPF",
      sequence: 4,
    },
  ],
};

function norm(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function normName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function buildStructureExport(template, mappings, resolveComponent) {
  const components = (mappings || []).map((m, idx) => {
    const master = resolveComponent?.(m.component_id) || {};
    return {
      code: master.code || null,
      name: master.name || null,
      calculation_source: m.calculation_source || "fixed_amount",
      amount: m.amount ?? null,
      percentage: m.percentage ?? null,
      percentage_of: m.percentage_of ?? null,
      reference: m.reference ?? null,
      sequence: m.sequence ?? idx + 1,
    };
  });

  return {
    version: STRUCTURE_IMPORT_VERSION,
    name: template?.name || "Exported Structure",
    description: template?.description || null,
    exported_at: new Date().toISOString(),
    components,
  };
}

export function downloadJson(filename, payload) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function parseStructureImportFile(text) {
  let raw;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error("Invalid JSON file. Export a structure or use the sample format.");
  }

  if (Array.isArray(raw)) {
    raw = { version: STRUCTURE_IMPORT_VERSION, name: "Imported Structure", components: raw };
  }

  if (!raw || typeof raw !== "object") {
    throw new Error("Structure file must be a JSON object.");
  }

  const name = String(raw.name || "").trim();
  if (!name) throw new Error("Structure file is missing a name.");

  const components = Array.isArray(raw.components) ? raw.components : [];
  if (!components.length) {
    throw new Error("Structure file has no components to import.");
  }

  const normalized = components.map((c, idx) => ({
    code: c.code ? String(c.code).trim() : null,
    name: c.name ? String(c.name).trim() : null,
    calculation_source: c.calculation_source || (c.percentage != null ? "percentage" : "fixed_amount"),
    amount: c.amount ?? null,
    percentage: c.percentage ?? null,
    percentage_of: c.percentage_of ?? null,
    reference: c.reference ?? null,
    sequence: c.sequence ?? idx + 1,
  }));

  return {
    version: Number(raw.version) || STRUCTURE_IMPORT_VERSION,
    name,
    description: raw.description ? String(raw.description).trim() : null,
    components: normalized,
  };
}

/**
 * Match import rows to master components by code, then name.
 * Returns { matched, missing, rows } where rows include component_id when found.
 */
export function matchImportComponents(parsed, masterComponents) {
  const byCode = new Map();
  const byName = new Map();
  (masterComponents || []).forEach((c) => {
    if (c.code) byCode.set(norm(c.code), c);
    if (c.name) byName.set(normName(c.name), c);
  });

  const matched = [];
  const missing = [];
  const rows = (parsed.components || []).map((row) => {
    const hit =
      (row.code && byCode.get(norm(row.code))) ||
      (row.name && byName.get(normName(row.name))) ||
      null;

    if (hit) {
      matched.push({ ...row, component_id: hit.component_id, resolved_name: hit.name, resolved_code: hit.code });
      return { ...row, component_id: hit.component_id, resolved_name: hit.name, resolved_code: hit.code, status: "ok" };
    }
    missing.push(row);
    return { ...row, component_id: null, status: "missing" };
  });

  return { matched, missing, rows };
}

export function toAddComponentPayload(templateId, row) {
  const body = {
    template_id: templateId,
    component_id: row.component_id,
    calculation_source: row.calculation_source || "fixed_amount",
    sequence: row.sequence ?? undefined,
  };
  if (row.amount != null && row.amount !== "") body.amount = Number(row.amount);
  if (row.percentage != null && row.percentage !== "") body.percentage = Number(row.percentage);
  if (row.percentage_of) body.percentage_of = row.percentage_of;
  if (row.reference) body.reference = row.reference;
  return body;
}
